"use client";

import { useEffect, useState } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import { useParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Calendar,
  CalendarCheck2,
  ArrowLeft,
  MapPin,
  Phone,
  Ticket,
  Music,
  Utensils,
  HelpCircle,
  Share2,
  Flame,
  Trophy,
  Tags,
  CalendarHeart,
  Handshake,
  PartyPopper,
} from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { buildMapsHrefForLocation, getLocationBySlug } from "@/lib/restaurant-platform";
import { optimizeCloudinaryUrl } from "@/lib/cloudinary";
import { getPublicPageBrand } from "@/lib/public-brand-theme";
import {
  isLocalPreviewPublicHost,
  publicLocationCookieName,
} from "@/lib/public-location-cookie";
import {
  setBrowserPublicLocationIdOverride,
  useBrowserPublicLocationId,
} from "@/lib/use-browser-public-location-id";
import { parseLocalDate } from "@/lib/utils";
import type { Doc } from "../../../../../convex/_generated/dataModel";
import { analytics } from "@/lib/analytics";
import { toast } from "sonner";
import { RSVPModal } from "@/components/events/rsvp-modal";
import { resolveEventVenue } from "@/lib/event-venue";
import { formatSeatingTime } from "@/lib/event-seatings";
import {
  firstPartyTicketGuestLabel,
  freeRsvpGuestLabel,
  resolvePublicEventAdmission,
  resolveTicketRouteSlug,
} from "@/components/event-tickets/event-ticket-public-controller";
import {
  formatEventWhenLine,
  isTitleBoilerplateDescription,
  splitMenuHighlights,
} from "./event-detail-presentation";

function formatPhoneDisplay(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  if (normalized.length !== 10) return phone;

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

// Get event type configuration
function getEventTypeConfig(eventType?: string) {
  switch (eventType) {
    case "special_dinner":
      return {
        icon: Utensils,
        label: "Special Dinner",
        color: "bg-rose-500",
      };
    case "trivia":
      return {
        icon: HelpCircle,
        label: "Trivia Night",
        color: "bg-cyan-700",
      };
    case "taco_tuesday":
      return {
        icon: Flame,
        label: "Taco Tuesday",
        color: "bg-lime-700",
      };
    case "watch_party":
      return {
        icon: Trophy,
        label: "Watch Party",
        color: "bg-sky-700",
      };
    case "happy_hour":
      return {
        icon: Tags,
        label: "Happy Hour",
        color: "bg-teal-700",
      };
    case "holiday":
      return {
        icon: CalendarHeart,
        label: "Holiday",
        color: "bg-red-800",
      };
    case "community":
      return {
        icon: Handshake,
        label: "Community",
        color: "bg-indigo-700",
      };
    case "social":
      return {
        icon: PartyPopper,
        label: "Social",
        color: "bg-fuchsia-700",
      };
    case "live_music":
    default:
      return {
        icon: Music,
        label: "Live Music",
        color: "bg-amber-500",
      };
  }
}

function resolveEventLocation(locationId?: string) {
  if (!locationId) return null;

  try {
    return getLocationBySlug(locationId);
  } catch {
    return null;
  }
}

type EventDetailClientProps = {
  initialLocationId: string;
  initialEvent?: Doc<"events"> | null;
};

export function EventDetailClient({
  initialLocationId,
  initialEvent,
}: EventDetailClientProps) {
  const params = useParams();
  const slug = params.slug as string;
  // Host-aware fallback brand/location. When the event has no resolvable
  // location, the displayed name/address/phone fall back to this host's tenant
  // (resolved from the request via the marketing context) rather than the
  // build-time env tenant, so Roma's host never shows Tuscan fallbacks.
  const { siteConfig } = useMarketingLocationContext();
  const [rsvpOpen, setRsvpOpen] = useState(false);
  const publicLocationId = useBrowserPublicLocationId(initialLocationId);
  const liveEvent = useQuery(api.events.getBySlug, {
    slug,
    locationId: publicLocationId,
  });
  const event = liveEvent !== undefined ? liveEvent : initialEvent;
  const admissionRouteSlug = event
    ? resolveTicketRouteSlug(event.slug, slug)
    : slug;
  const admission = event
    ? resolvePublicEventAdmission({
        status: event.status,
        admissionMode: event.admissionMode,
        ticketLink: event.ticketLink,
        slug: admissionRouteSlug,
      })
    : { kind: "none" as const };
  const admissionMode = admission.kind;
  const upcomingEvents = useQuery(
    api.events.getUpcoming,
    event ? { limit: 3, locationId: event.locationId } : "skip",
  );
  const rsvpAvailability = useQuery(
    api.eventRsvps.getAvailability,
    event?.status === "published" && admissionMode === "free_rsvp"
      ? { eventId: event._id, locationId: event.locationId }
      : "skip",
  );
  const eventLocation = resolveEventLocation(event?.locationId);

  useEffect(() => {
    if (!event?.locationId || !eventLocation) return;

    setBrowserPublicLocationIdOverride(event.locationId);

    const pageBrandElement = document.querySelector<HTMLElement>(
      "[data-public-page-brand]",
    );
    const previousPageBrand = pageBrandElement?.getAttribute(
      "data-public-page-brand",
    );
    const pageBrand = getPublicPageBrand({ name: eventLocation.name });

    pageBrandElement?.setAttribute("data-public-page-brand", pageBrand);

    if (isLocalPreviewPublicHost(window.location.origin)) {
      document.cookie = `${publicLocationCookieName}=${encodeURIComponent(
        event.locationId,
      )}; path=/; max-age=${60 * 60 * 24 * 7}; samesite=lax`;
    }

    return () => {
      setBrowserPublicLocationIdOverride(undefined);

      if (!pageBrandElement) return;

      if (previousPageBrand) {
        pageBrandElement.setAttribute("data-public-page-brand", previousPageBrand);
      } else {
        pageBrandElement.removeAttribute("data-public-page-brand");
      }
    };
  }, [event?.locationId, eventLocation]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: event?.title,
          text: event?.description,
          url: window.location.href,
        });
      } catch (err) {
        console.error("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  // Loading state — no H1. The real title is server-rendered when
  // initialEvent is present; a slug-derived heading would mismatch title.
  if (event === undefined) {
    return (
      <div className="roma-paper min-h-screen animate-pulse">
        <div className="container mx-auto max-w-3xl space-y-3 px-4 py-4 sm:px-6">
          <div className="h-4 w-32 rounded bg-[var(--roma-linen)]" />
          <div className="h-8 w-3/4 rounded bg-[var(--roma-linen)]" />
          <div className="h-5 w-1/2 rounded bg-[var(--roma-linen)]" />
          <div className="space-y-2 pt-2">
            <div className="h-4 w-full rounded bg-[var(--roma-linen)]" />
            <div className="h-4 w-2/3 rounded bg-[var(--roma-linen)]" />
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (event === null) {
    return (
      <div className="roma-paper flex min-h-[70vh] items-center px-4 py-20 sm:px-6">
        <div className="mx-auto w-full max-w-xl border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] p-6 text-left shadow-[0_24px_70px_-52px_oklch(0.24_0.03_55_/_0.35)] sm:p-8">
          <div className="roma-icon-chip mb-6 flex h-16 w-16 items-center justify-center rounded-full">
            <Calendar className="h-8 w-8" />
          </div>
          <p className="roma-eyebrow text-[var(--roma-terracotta)]">
            <span>Events</span>
          </p>
          <h1 className="mt-4 font-serif text-3xl font-normal tracking-tight text-[var(--foreground)]">
            Event not found.
          </h1>
          <p className="roma-text-muted mt-3 max-w-md leading-7">
            This event may have ended or the link is incorrect. Check out our upcoming events below.
          </p>
          <Button asChild className="roma-cta mt-6 rounded-full">
            <Link href="/events">View All Events</Link>
          </Button>
        </div>
      </div>
    );
  }

  const eventDate = parseLocalDate(event.date);
  const typeConfig = getEventTypeConfig(event.eventType);
  const TypeIcon = typeConfig.icon;
  const locationName = eventLocation ? eventLocation.name : siteConfig.name;
  const locationAddressLine1 = eventLocation
    ? eventLocation.address.line1
    : siteConfig.address.line1;
  const locationAddressCity = eventLocation
    ? eventLocation.address.city
    : siteConfig.address.city;
  const locationAddressState = eventLocation
    ? eventLocation.address.state
    : siteConfig.address.state;
  const locationAddressZip = eventLocation
    ? eventLocation.address.zip
    : siteConfig.address.zip;
  const locationPhone = eventLocation ? eventLocation.contact.phone : siteConfig.contact.phone;
  const locationMapsHref = eventLocation
    ? buildMapsHrefForLocation(eventLocation)
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${siteConfig.address.line1}, ${siteConfig.address.city}, ${siteConfig.address.state}`,
      )}`;

  // Check if event is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = eventDate < today;
  // Legacy behavior: const isRsvpEligible = event.status === "published";
  // Explicit admission mode prevents paid/external events from opening RSVP.
  const isRsvpEligible = admissionMode === "free_rsvp" && event.status === "published";
  const firstPartyTicketHref =
    admission.kind === "first_party_ticket"
      ? admission.href
      : `/events/${event.slug ?? slug}/tickets`;
  const firstPartyTicketCta = firstPartyTicketGuestLabel({
    ticketSalesPaused: event.ticketSalesPaused,
    ticketPriceCents: event.ticketPriceCents,
  });
  const firstPartyTicketCtaLabel = firstPartyTicketCta.label;
  const rsvpCta = freeRsvpGuestLabel({
    ticketSalesPaused: event.ticketSalesPaused,
  });
  const isRsvpPaused = rsvpCta.paused;
  const hasLegacyTicketPrice =
    admissionMode !== "first_party_ticket" &&
    (event.ticketPrice ?? 0) > 0 &&
    !event.pricePerPerson;
  const isRsvpAvailabilityLoading =
    isRsvpEligible && !isRsvpPaused && rsvpAvailability === undefined;
  const remainingGuests = rsvpAvailability?.remainingGuests;
  const hasRsvpCapacityLimit = remainingGuests !== undefined;
  const isRsvpFull = !isRsvpPaused && (rsvpAvailability?.isFull ?? false);
  const maxPartySize = hasRsvpCapacityLimit ? Math.min(Math.max(remainingGuests, 0), 10) : 10;
  const isRsvpDisabled =
    isRsvpAvailabilityLoading || isRsvpFull || isRsvpPaused;
  const rsvpButtonLabel = isRsvpPaused
    ? rsvpCta.label
    : isRsvpAvailabilityLoading
      ? "Checking Availability"
      : isRsvpFull
        ? "Guest List Full"
        : rsvpCta.label;
  const rsvpStatusText = isRsvpPaused
    ? "Sold out."
    : isRsvpAvailabilityLoading
      ? "Checking guest-list availability."
      : isRsvpFull
        ? "The guest list is full."
        : hasRsvpCapacityLimit
          ? `${remainingGuests} ${remainingGuests === 1 ? "spot" : "spots"} available.`
          : "The guest list is open.";
  const rsvpHelpText = isRsvpPaused
    ? "The guest list is closed for now."
    : isRsvpAvailabilityLoading
      ? "We're checking the guest list before opening RSVPs."
      : isRsvpFull
        ? "Call us if you want to ask about cancellations."
        : "Add your party size and we'll email a confirmation. Tables remain first come, first served.";
  const stickyRsvpStatus = isRsvpPaused
    ? "Sold out"
    : isRsvpAvailabilityLoading
      ? "Checking availability"
      : isRsvpFull
        ? "Guest list is currently full"
        : remainingGuests !== undefined && remainingGuests <= 10
          ? `Only ${remainingGuests} ${remainingGuests === 1 ? "spot" : "spots"} left`
          : "Guest list is open";
  const locationPhoneDisplay = formatPhoneDisplay(locationPhone);
  const venue = resolveEventVenue({
    romaBranch: event.romaBranch,
    locationId: event.locationId,
    fallback: {
      name: locationName,
      addressLine1: locationAddressLine1,
      addressLine2: `${locationAddressCity}, ${locationAddressState} ${locationAddressZip}`,
      phoneDisplay: locationPhoneDisplay,
      phoneHref: locationPhone,
      mapsUrl: locationMapsHref,
    },
  });
  const seatingsLine =
    event.seatingTimes && event.seatingTimes.length > 1
      ? `Seatings: ${event.seatingTimes.map(formatSeatingTime).join(", ")}`
      : null;

  const handleRsvpClick = (source: "event_detail" | "event_detail_mobile") => {
    if (isRsvpDisabled) return;
    analytics.reservationClicked(source, {
      location: event.locationId,
      cta_text: rsvpButtonLabel,
      lead_type: "event_rsvp",
    });
    analytics.eventRsvpStarted(event.title, {
      event_id: event._id,
      event_type: event.eventType,
      location_id: event.locationId,
      source,
    });
    setRsvpOpen(true);
  };

  const trackPhoneClick = (source: string, ctaText: string, phone: string = locationPhone) => {
    analytics.phoneClicked({
      source,
      location: event.locationId,
      cta_text: ctaText,
      destination_url: `tel:${phone}`,
    });
  };

  // Filter out current event from related events
  const relatedEvents = upcomingEvents?.filter((e) => e._id !== event._id).slice(0, 2);
  const whenLine = formatEventWhenLine({
    date: eventDate,
    startTime: event.startTime,
    endTime: event.endTime,
  });
  const menuItems = event.menuHighlight
    ? splitMenuHighlights(event.menuHighlight)
    : [];
  const showAbout = !isTitleBoilerplateDescription(event.description, event.title);
  const ticketPriceLabel =
    admissionMode === "first_party_ticket" &&
    typeof event.ticketPriceCents === "number"
      ? `$${(event.ticketPriceCents / 100).toFixed(2)} per wristband`
      : event.pricePerPerson
        ? `$${event.pricePerPerson} per person`
        : hasLegacyTicketPrice
          ? `$${event.ticketPrice}`
          : null;

  return (
    <div className="roma-paper min-h-screen pb-[max(8rem,calc(env(safe-area-inset-bottom,0px)+7.5rem))] md:pb-20">
      <div className="container relative z-10 mx-auto max-w-3xl px-4 py-3 sm:px-6 sm:py-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            size="sm"
            className="min-h-11 rounded-full border-[var(--roma-parchment-line)] bg-transparent px-4 text-[var(--foreground)] hover:bg-[var(--roma-linen)] sm:min-h-9"
            asChild
          >
            <Link href="/events">
              <ArrowLeft className="mr-2 h-4 w-4" /> All Events
            </Link>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="min-h-11 min-w-11 rounded-full border-[var(--roma-parchment-line)] bg-transparent text-[var(--foreground)] hover:bg-[var(--roma-linen)] sm:min-h-9 sm:min-w-9"
            onClick={handleShare}
            aria-label="Share event"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        <motion.div
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <article>
            <div data-event-first-fold="">
            <p className="roma-eyebrow text-[var(--roma-terracotta)]">
              <TypeIcon className="h-3 w-3" />
              <span>{typeConfig.label}</span>
            </p>
            <h1 className="mt-2 font-serif text-3xl font-normal leading-tight tracking-tight text-[var(--foreground)] sm:text-4xl">
              {event.title}
            </h1>
            {(event.isVip || isPast) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {event.isVip && (
                  <Badge className="rounded-full border border-[var(--roma-saffron)]/45 bg-[var(--roma-saffron)]/18 px-3 py-1 text-[var(--foreground)] shadow-none">
                    VIP Event
                  </Badge>
                )}
                {isPast && (
                  <Badge
                    variant="outline"
                    className="rounded-full border-[var(--roma-parchment-line)] bg-transparent px-3 py-1 text-[var(--foreground)]/58"
                  >
                    Past Event
                  </Badge>
                )}
              </div>
            )}
            <p className="mt-2 font-serif text-lg font-normal leading-snug text-[var(--foreground)] sm:text-xl">
              {/* Break only between date parts, never inside "6:00 PM – 9:00 PM". */}
              {whenLine.split(" · ").map((part, index) => (
                <span key={part}>
                  {index > 0 && " · "}
                  <span className="whitespace-nowrap">{part}</span>
                </span>
              ))}
            </p>
            {seatingsLine && (
              <p className="mt-1 text-sm text-[var(--foreground)]/62">{seatingsLine}</p>
            )}

            {menuItems.length > 0 && (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-base leading-6 text-[var(--foreground)]/82">
                {menuItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}

            {ticketPriceLabel && (
              <div className="mt-3">
                <p className="flex items-center gap-2 font-serif text-2xl font-normal text-[var(--foreground)]">
                  <Ticket className="h-5 w-5 text-[var(--roma-terracotta)]" />
                  {ticketPriceLabel}
                </p>
                {admissionMode === "first_party_ticket" &&
                  typeof event.ticketPriceCents === "number" && (
                    <p className="roma-text-muted mt-1.5 text-sm">
                      Tax is calculated at checkout when applicable.
                    </p>
                  )}
              </div>
            )}

            {isRsvpEligible && (
              <div
                id="rsvp"
                className="mt-3 scroll-mt-24 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/45 p-3 sm:p-4"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--roma-terracotta)] sm:text-sm">
                  RSVP list
                </p>
                <p className="mt-1.5 font-serif text-xl font-normal text-[var(--foreground)] sm:mt-2 sm:text-2xl">
                  {rsvpStatusText}
                </p>
                <p className="roma-text-muted mt-1.5 text-sm leading-6 sm:mt-2 sm:text-base sm:leading-7">
                  {rsvpHelpText}
                </p>
              </div>
            )}

            {!isPast && (
              <div className="mt-4 hidden md:block" data-event-primary-cta="">
                {isRsvpEligible ? (
                  isRsvpPaused ? (
                    <Button
                      size="lg"
                      className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/10 md:max-w-md"
                      disabled
                      data-ticket-sales-paused=""
                    >
                      <CalendarCheck2 className="h-5 w-5" />
                      {rsvpButtonLabel}
                    </Button>
                  ) : isRsvpFull ? (
                    <Button
                      size="lg"
                      variant="outline"
                      className="h-14 w-full rounded-full border-[var(--roma-parchment-line)] bg-transparent text-base font-semibold text-[var(--foreground)] hover:bg-[var(--roma-linen)] md:max-w-md"
                      asChild
                    >
                      <a
                        href={`tel:${locationPhone}`}
                        aria-label={`Call ${locationName} about event cancellations at ${locationPhoneDisplay}`}
                        onClick={() =>
                          trackPhoneClick(
                            "event_detail_cancellations",
                            "Call About Cancellations",
                          )
                        }
                      >
                        <Phone className="h-5 w-5" />
                        Call About Cancellations
                      </a>
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/10 active:scale-[0.96] md:max-w-md"
                      disabled={isRsvpDisabled}
                      onClick={() => handleRsvpClick("event_detail")}
                    >
                      <CalendarCheck2 className="h-5 w-5" />
                      {rsvpButtonLabel}
                    </Button>
                  )
                ) : admissionMode === "first_party_ticket" ? (
                  firstPartyTicketCta.paused ? (
                    <Button
                      size="lg"
                      className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/10 md:max-w-md"
                      disabled
                      data-ticket-sales-paused=""
                    >
                      <Ticket className="h-5 w-5" />
                      {firstPartyTicketCtaLabel}
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/10 active:scale-[0.96] md:max-w-md"
                      asChild
                    >
                      <Link href={firstPartyTicketHref}>
                        <Ticket className="h-5 w-5" />
                        {firstPartyTicketCtaLabel}
                      </Link>
                    </Button>
                  )
                ) : admissionMode === "external_ticket" ? (
                  <Button
                    size="lg"
                    className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/10 active:scale-[0.96] md:max-w-md"
                    asChild
                  >
                    <a
                      href={event.ticketLink}
                      target="_blank"
                      rel="noopener"
                      onClick={() => analytics.reservationClicked("event_detail")}
                    >
                      <Ticket className="h-5 w-5" />
                      Get Tickets
                    </a>
                  </Button>
                ) : admissionMode === "none" ? null : null}
              </div>
            )}

            </div>
            <div data-event-after-fold="">
            {event.imageUrl && (
              <div className="mt-6 overflow-hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] sm:mt-8">
                {/* Event images can come from Convex storage or an external image host. */}
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={optimizeCloudinaryUrl(event.imageUrl, 1200)}
                  alt={event.title}
                  className="mx-auto block h-auto w-full object-contain md:max-h-[min(80vh,56rem)] md:w-auto"
                />
              </div>
            )}

            {showAbout && event.description && (
              <div className="mt-6 sm:mt-8">
                <h2 className="mb-2 font-serif text-xl font-normal text-[var(--foreground)] sm:mb-3 sm:text-2xl">
                  About this event
                </h2>
                <p className="roma-text-muted whitespace-pre-wrap text-sm leading-6 sm:text-base sm:leading-7">
                  {event.description}
                </p>
              </div>
            )}

            <div className="mt-6 border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/35 p-4 sm:mt-8 sm:p-5">
                <div className="flex items-start gap-2.5 sm:gap-3">
                  <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-[var(--roma-terracotta)]" />
                  <div>
                    <p className="font-semibold text-[var(--foreground)]">{venue.name}</p>
                    <p className="text-sm text-[var(--foreground)]/62">{venue.addressLine1}</p>
                    <p className="text-sm text-[var(--foreground)]/62">{venue.addressLine2}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <a
                    href={venue.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-full border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/55 px-3 text-sm font-semibold text-[var(--foreground)] transition-colors hover:bg-[var(--roma-linen)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roma-terracotta)] focus-visible:ring-offset-2"
                    aria-label={`Get directions to ${venue.name}`}
                    onClick={() =>
                      analytics.directionsClicked({
                        source: "event_detail",
                        location: event.locationId,
                        cta_text: "Directions",
                        destination_url: venue.mapsUrl,
                      })
                    }
                  >
                    <MapPin className="h-4 w-4 text-[var(--roma-terracotta)]" />
                    Directions
                  </a>
                  <a
                    href={`tel:${venue.phoneHref}`}
                    className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-full border border-[var(--roma-parchment-line)] bg-transparent px-3 text-sm font-semibold text-[var(--foreground)] transition-colors hover:bg-[var(--roma-linen)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--roma-terracotta)] focus-visible:ring-offset-2"
                    aria-label={`Call ${venue.name} at ${venue.phoneDisplay}`}
                    onClick={() =>
                      trackPhoneClick("event_detail_location", "Call Us", venue.phoneHref)
                    }
                  >
                    <Phone className="h-4 w-4 text-[var(--roma-terracotta)]" />
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </article>

          {/* Related Events */}
          {relatedEvents && relatedEvents.length > 0 && (
            <div className="mt-8 sm:mt-12">
              <p className="roma-eyebrow text-[var(--roma-terracotta)]">
                <span>More Nights</span>
              </p>
              <h2 className="mb-4 mt-3 font-serif text-2xl font-normal tracking-tight text-[var(--foreground)] sm:mb-6 sm:mt-4 sm:text-4xl">
                More upcoming events.
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
                {relatedEvents.map((relatedEvent) => {
                  const relatedDate = parseLocalDate(relatedEvent.date);
                  const relatedTypeConfig = getEventTypeConfig(relatedEvent.eventType);
                  const RelatedIcon = relatedTypeConfig.icon;

                  return (
                    <Link
                      key={relatedEvent._id}
                      href={relatedEvent.slug ? `/events/${relatedEvent.slug}` : "/events"}
                      className="group"
                    >
                      <Card className="h-full rounded-none border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)] py-0 shadow-none transition-colors hover:bg-[var(--roma-linen)]">
                        <CardContent className="p-4 sm:p-5">
                          <div className="flex items-start gap-4">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden border border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]">
                              {relatedEvent.imageUrl ? (
                                <>
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={optimizeCloudinaryUrl(relatedEvent.imageUrl, 112)}
                                    alt={relatedEvent.title}
                                    className="h-full w-full object-cover saturate-[0.92]"
                                  />
                                </>
                              ) : (
                                <RelatedIcon className="h-6 w-6 text-[var(--roma-terracotta)]" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="line-clamp-1 font-serif text-lg font-normal text-[var(--foreground)] transition-colors group-hover:text-[var(--roma-terracotta)]">
                                {relatedEvent.title}
                              </p>
                              <p className="text-sm text-[var(--foreground)]/58">
                                {relatedDate.toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric",
                                })}
                              </p>
                              <Badge
                                variant="outline"
                                className="mt-2 rounded-full border-[var(--roma-parchment-line)] bg-transparent text-xs text-[var(--foreground)]/62"
                              >
                                {relatedTypeConfig.label}
                              </Badge>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-6 text-left">
                <Button
                  variant="outline"
                  asChild
                  className="rounded-full border-[var(--roma-parchment-line)] bg-transparent text-[var(--foreground)] hover:bg-[var(--roma-linen)]"
                >
                  <Link href="/events">View All Events</Link>
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Mobile Sticky CTA */}
      {!isPast && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--roma-parchment-line)] bg-[var(--roma-ivory)]/94 px-4 pb-[var(--sticky-bar-pb)] pt-2.5 shadow-[0_-14px_34px_-24px_oklch(0.24_0.03_55_/_0.45)] backdrop-blur-xl md:hidden">
          <div className="mx-auto max-w-lg">
            {isRsvpEligible ? (
              <>
                <p
                  aria-live="polite"
                  className="mb-2 flex items-center justify-center gap-2 text-center text-xs font-semibold text-[var(--foreground)]/68"
                >
                  <span
                    aria-hidden="true"
                    className={`h-1.5 w-1.5 rounded-full ${
                      isRsvpPaused
                        ? "bg-zinc-400"
                        : isRsvpAvailabilityLoading
                          ? "bg-amber-400"
                          : isRsvpFull
                            ? "bg-zinc-400"
                            : "bg-emerald-500"
                    }`}
                  />
                  {stickyRsvpStatus}
                </p>
                {isRsvpPaused ? (
                  <Button
                    className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15"
                    disabled
                    data-ticket-sales-paused=""
                  >
                    <CalendarCheck2 className="h-5 w-5" />
                    {rsvpButtonLabel}
                  </Button>
                ) : isRsvpFull ? (
                  <Button
                    variant="outline"
                    className="h-14 w-full rounded-full border-[var(--roma-parchment-line)] bg-[var(--roma-linen)]/55 text-base font-semibold text-[var(--foreground)] shadow-sm"
                    asChild
                  >
                    <a
                      href={`tel:${locationPhone}`}
                      aria-label={`Call ${locationName} about event cancellations at ${locationPhoneDisplay}`}
                      onClick={() =>
                        trackPhoneClick(
                          "event_detail_mobile_cancellations",
                          "Call About Cancellations",
                        )
                      }
                    >
                      <Phone className="h-5 w-5" />
                      Call About Cancellations
                    </a>
                  </Button>
                ) : (
                  <Button
                    className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15 active:scale-[0.96]"
                    disabled={isRsvpDisabled}
                    onClick={() => handleRsvpClick("event_detail_mobile")}
                  >
                    <CalendarCheck2 className="h-5 w-5" />
                    {rsvpButtonLabel}
                  </Button>
                )}
              </>
            ) : admissionMode === "first_party_ticket" ? (
              firstPartyTicketCta.paused ? (
                <Button
                  className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15"
                  disabled
                  data-ticket-sales-paused=""
                >
                  <Ticket className="h-5 w-5" />
                  {firstPartyTicketCtaLabel}
                </Button>
              ) : (
                <Button
                  className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15 active:scale-[0.96]"
                  asChild
                >
                  <Link href={firstPartyTicketHref}>
                    <Ticket className="h-5 w-5" />
                    {firstPartyTicketCtaLabel}
                  </Link>
                </Button>
              )
            ) : admissionMode === "external_ticket" ? (
              <Button
                className="roma-cta h-14 w-full rounded-full text-base font-semibold shadow-lg shadow-black/15 active:scale-[0.96]"
                asChild
              >
                <a
                  href={event.ticketLink}
                  target="_blank"
                  rel="noopener"
                  onClick={() => analytics.reservationClicked("event_detail_mobile")}
                >
                  Get Tickets
                </a>
              </Button>
            ) : admissionMode === "none" ? null : null}
          </div>
        </div>
      )}
      {isRsvpEligible && (
        <RSVPModal
          event={event}
          maxPartySize={maxPartySize}
          open={rsvpOpen}
          onOpenChange={setRsvpOpen}
        />
      )}
    </div>
  );
}
